import { useQueries } from '@mint-lib/query-client';
import { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import {
  EntityStatuses,
  ApprovalProcessStatuses,
} from '../constants/shared.js';
import type { EntitySpecificFields } from '../types/shared.js';
import {
  resolveProcessStatusFromProcessStatusData,
  resolveUserStatusFromProcessStatusData,
} from '../utils/shared';
import useApprovalSocketConnection from './useApprovalSocketConnection.js';

type UseApprovalFlowStatusesQuery = Pick<
  EntitySpecificFields,
  'entityId' | 'entityType' | 'entitySource'
>;

export type UseApprovalFlowStatusesOptions = {
  /** Entities to fetch approval flow statuses for. */
  entities: Array<
    Pick<EntitySpecificFields, 'entityId' | 'entityType' | 'entitySource'>
  >;
  /** Whether the underlying queries should run. Defaults to `true`. */
  enabled?: boolean;
};

type UseApprovalFlowStatusesPendingResult = {
  isPending: true;
  isError: false;
  data: null;
};

type UseApprovalFlowStatusesErrorResult = {
  isPending: false;
  isError: true;
  data: null;
};

type UseApprovalFlowStatusesSuccessResult = {
  isPending: false;
  isError: false;
  data: Array<
    UseApprovalFlowStatusesQuery & {
      userStatus: EntityStatuses;
      approvalProcessId: string | null;
      approvalProcessStatus: ApprovalProcessStatuses;
      isLocked: boolean;
    }
  >;
};

export type UseApprovalFlowStatusesResult =
  | UseApprovalFlowStatusesPendingResult
  | UseApprovalFlowStatusesErrorResult
  | UseApprovalFlowStatusesSuccessResult;

/**
 * Fetches and combines approval flow statuses for a list of entities,
 * keeping their approval sockets subscribed while they're in use.
 */
const useApprovalFlowStatuses = ({
  entities,
  enabled = true,
}: UseApprovalFlowStatusesOptions): UseApprovalFlowStatusesResult => {
  useApprovalSocketConnection(entities);

  // TODO: integrate bulk checkFlowStatus endpoint when it's ready
  return useQueries({
    queries: entities.map((entity) => ({
      ...ApprovalFlows.checkFlowStatus({ params: entity }),
      staleTime: 30_000,
      enabled,
      select: (data: ApprovalFlows.CheckFlowStatusResponse) =>
        data.data?.items?.[0],
    })),
    combine: (results): UseApprovalFlowStatusesResult => {
      if (results.some((result) => result.isError)) {
        return {
          isPending: false,
          isError: true,
          data: null,
        };
      }

      if (results.some((result) => result.isPending)) {
        return {
          isPending: true,
          isError: false,
          data: null,
        };
      }

      return {
        isPending: false,
        isError: false,
        data: results.map((result, index) => {
          const entity = entities[index];

          return {
            ...entity,
            approvalProcessId: result.data?.requestId ?? null,
            approvalProcessStatus: resolveProcessStatusFromProcessStatusData(
              result.data,
            ),
            userStatus: resolveUserStatusFromProcessStatusData(result.data),
            isLocked: result.data?.requestStatus === 'pending',
          };
        }),
      };
    },
  });
};

export default useApprovalFlowStatuses;
